import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { ShieldAlert, Sparkles, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { JobProgress, useJob } from "@/components/common/Common";
import { useConfirm } from "@/components/common/useConfirm";
import { api, errMsg } from "@/lib/api";

const ST = { DISAPPROVED: "Reprovado", APPROVED_LIMITED: "Aprovado com restrições" };

export const Fields = ({ items, max, onChange, prefix }) => (
  <div className="grid sm:grid-cols-2 gap-2">{items.map((t, i) => (
    <div key={`${prefix}-${i}`} className="relative"><Input value={t} onChange={(e) => onChange(items.map((x, j) => (j === i ? e.target.value : x)))} className={`pr-12 h-9 ${t.length > max ? "border-rose-500" : ""}`} data-testid={`policy-${prefix}-${i}`} />
      <span className={`absolute right-2 top-2.5 text-[10px] font-mono ${t.length > max ? "text-rose-500" : "text-muted-foreground"}`}>{t.length}/{max}</span></div>))}</div>
);

function FixEditor({ cid, ad, onDone }) {
  const [jobId, setJobId] = useState(null);
  const [s, setS] = useState(null);
  const [sending, setSending] = useState(false);
  const [confirm, dialog] = useConfirm();
  const job = useJob(jobId, (j) => { setJobId(null); j.status === "done" ? setS(j.result) : toast.error(j.error); });
  const suggest = async () => { try { setJobId((await api.post(`/campaigns/${cid}/policy/suggest`, { ad_id: ad.ad_id })).data.job_id); } catch (e) { toast.error(errMsg(e)); } };
  const bad = s && (s.headlines.some((h) => h.length > 30) || s.descriptions.some((d) => d.length > 90));
  const send = async () => {
    if (!(await confirm({ title: "Enviar correção ao Google Ads?", desc: "Os títulos e descrições deste anúncio serão substituídos no Google Ads e ele passará por uma nova revisão automática.", ok: "Enviar correção" }))) return;
    setSending(true);
    try { const { data } = await api.post(`/campaigns/${cid}/policy/apply`, { ad_id: ad.ad_id, headlines: s.headlines, descriptions: s.descriptions }); toast.success(data.message); setS(null); onDone(); }
    catch (e) { toast.error(errMsg(e)); } finally { setSending(false); }
  };
  return (
    <div className="space-y-3">
      {dialog}
      {!s && !jobId && <Button size="sm" className="rounded-full" onClick={suggest} data-testid={`policy-fix-${ad.ad_id}`}><Sparkles className="h-4 w-4 mr-1" />Corrigir com IA</Button>}
      {jobId && <JobProgress job={job || { status: "running", progress: 5, message: "Reescrevendo..." }} />}
      {s && <div className="space-y-3 rounded-lg border p-3" data-testid={`policy-suggestion-${ad.ad_id}`}>
        {s.explanation && <p className="text-xs text-muted-foreground">{s.explanation}</p>}
        <p className="eyebrow">Novos títulos</p><Fields items={s.headlines} max={30} prefix="headline" onChange={(headlines) => setS({ ...s, headlines })} />
        <p className="eyebrow">Novas descrições</p><Fields items={s.descriptions} max={90} prefix="description" onChange={(descriptions) => setS({ ...s, descriptions })} />
        <p className="text-[11px] text-muted-foreground">Texto gerado pela IA. Revise antes de enviar: a aprovação final é sempre do Google.</p>
        <div className="flex gap-2"><Button size="sm" className="rounded-full" disabled={sending || bad} onClick={send} data-testid={`policy-apply-${ad.ad_id}`}>{sending ? "Enviando..." : "Aprovar e enviar ao Google"}</Button>
          <Button size="sm" variant="ghost" className="rounded-full" onClick={() => setS(null)} data-testid={`policy-discard-${ad.ad_id}`}>Descartar</Button></div>
      </div>}
    </div>
  );
}

export default function PolicyFix({ cid }) {
  const [ads, setAds] = useState(null);
  const [err, setErr] = useState("");
  const load = useCallback(async () => {
    setErr("");
    try { setAds((await api.get(`/campaigns/${cid}/policy`)).data.ads); } catch (e) { setErr(errMsg(e)); setAds([]); }
  }, [cid]);
  useEffect(() => { load(); }, [load]);
  return (
    <div className="surface p-5 space-y-4" data-testid="policy-fix-panel">
      <div className="flex items-center gap-2"><ShieldAlert className="h-5 w-5 text-[#EA4335]" /><p className="font-semibold text-sm">Anúncios reprovados pelo Google</p>
        <Button variant="ghost" size="sm" className="ml-auto" onClick={load} data-testid="policy-refresh"><RefreshCw className="h-4 w-4 mr-1" />Atualizar</Button></div>
      {err && <p className="text-sm text-[#EA4335]" data-testid="policy-error">{err}</p>}
      {ads === null && <p className="text-sm text-muted-foreground">Consultando o Google Ads...</p>}
      {ads?.length === 0 && !err && <p className="text-sm text-muted-foreground" data-testid="policy-none">Nenhum anúncio reprovado nesta campanha.</p>}
      {ads?.map((ad) => (
        <div key={ad.ad_id} className="rounded-lg border p-4 space-y-3" data-testid={`policy-ad-${ad.ad_id}`}>
          <div className="flex flex-wrap items-center gap-2 text-sm"><span className="font-medium">{ad.group}</span>
            <span className="px-2 py-0.5 rounded-full text-xs bg-[#EA4335]/10 text-[#C5221F]">{ST[ad.status] || ad.status}</span>
            {ad.topics.map((t) => <span key={t.topic} className="px-2 py-0.5 rounded-full text-xs border">{t.topic}</span>)}</div>
          <p className="text-xs text-muted-foreground">{ad.headlines.slice(0, 3).join(" | ")}</p>
          <FixEditor cid={cid} ad={ad} onDone={load} />
        </div>
      ))}
    </div>
  );
}
